import { useState, useRef, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { X, Camera, Link as LinkIcon } from "lucide-react";
import { toHeaderSafeFileName } from "@/lib/header-safe-filename";
import { apiRequest } from "@/lib/queryClient";
import type { User } from "@shared/schema";
import {
  PROFILE_BACKGROUND_PRESETS,
  PROFILE_CARD_STYLES,
  PROFILE_FONT_PRESETS,
  profileThemeBackgroundCss,
  normalizeCardStyle,
  type ProfileTheme,
} from "@scroll/shared-types";
import { ACCENT_PRESETS } from "@scroll/shared-colors";

const CARD_STYLE_LABELS: Record<string, string> = { default: "Default", yourspace: "YourSpace" };
const FONT_LABELS: Record<string, string> = { default: "Default", serif: "Serif", mono: "Mono", rounded: "Rounded" };

function parseApiError(msg?: string): string {
  if (!msg) return "Failed to update profile";
  const match = msg.match(/^\d+:\s*(\{.+\})$/);
  if (match) try { return JSON.parse(match[1]).error; } catch {}
  return msg;
}

interface EditProfileModalProps {
  user: User;
  open: boolean;
  onClose: () => void;
}

export default function EditProfileModal({ user, open, onClose }: EditProfileModalProps) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [displayName, setDisplayName] = useState(user.displayName || "");
  const [username, setUsername] = useState(user.username || "");
  const [bio, setBio] = useState(user.bio || "");
  const [link, setLink] = useState(user.websiteUrl || "");
  const [previewUrl, setPreviewUrl] = useState(user.profileImageUrl || "");
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [accentColor, setAccentColor] = useState(user.accentColor || ACCENT_PRESETS[0]);
  const [theme, setTheme] = useState<ProfileTheme>((user.profileTheme as ProfileTheme) ?? {});

  // Username change cooldown (14 days)
  const usernameCooldownEnd = user.lastUsernameChangeAt
    ? new Date(new Date(user.lastUsernameChangeAt).getTime() + 14 * 24 * 60 * 60 * 1000)
    : null;
  const isUsernameOnCooldown = usernameCooldownEnd ? new Date() < usernameCooldownEnd : false;

  // Reset form when modal opens with fresh user data
  useEffect(() => {
    if (open) {
      setDisplayName(user.displayName || "");
      setUsername(user.username || "");
      setBio(user.bio || "");
      setLink(user.websiteUrl || "");
      setPreviewUrl(user.profileImageUrl || "");
      setAvatarFile(null);
      setUploadingAvatar(false);
      setUploadError("");
      setAccentColor(user.accentColor || ACCENT_PRESETS[0]);
      setTheme((user.profileTheme as ProfileTheme) ?? {});
    }
  }, [open, user]);

  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<User>) => {
      const res = await apiRequest("PATCH", "/api/users/me", updates);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      queryClient.invalidateQueries({ queryKey: [`/api/users/${user.id}`] });
      queryClient.invalidateQueries({ queryKey: [`/api/users/by-username/${user.username}`] });
      onClose();
    },
  });

  const [uploadError, setUploadError] = useState("");

  const handleSave = async () => {
    const updates: Partial<User> = {};
    if (displayName !== (user.displayName || "")) updates.displayName = displayName;
    if (username !== (user.username || "")) updates.username = username;
    if (bio !== (user.bio || "")) updates.bio = bio;
    if (link !== (user.websiteUrl || "")) updates.websiteUrl = link;
    if (accentColor !== (user.accentColor || ACCENT_PRESETS[0])) updates.accentColor = accentColor;

    const originalTheme = (user.profileTheme as ProfileTheme) ?? {};
    if (JSON.stringify(theme) !== JSON.stringify(originalTheme)) {
      updates.profileTheme = (Object.keys(theme).length > 0 ? theme : null) as User["profileTheme"];
    }

    if (avatarFile) {
      setUploadingAvatar(true);
      setUploadError("");
      try {
        const res = await fetch("/api/posts/upload-image", {
          method: "POST",
          headers: {
            "Content-Type": avatarFile.type || "image/jpeg",
            "X-File-Name": toHeaderSafeFileName(avatarFile.name, "avatar.jpg"),
          },
          body: avatarFile,
          credentials: "include",
        });
        if (!res.ok) throw new Error("Failed to upload image");
        const data = await res.json();
        updates.profileImageUrl = data.imageUrl;
      } catch (err) {
        setUploadError(err instanceof Error ? err.message : "Failed to upload image");
        setUploadingAvatar(false);
        return;
      }
      setUploadingAvatar(false);
    }

    if (Object.keys(updates).length === 0) {
      onClose();
      return;
    }

    updateMutation.mutate(updates);
  };

  if (!open) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-50 scroll-modal-overlay"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center pointer-events-none">
        <div
          className="pointer-events-auto w-full md:max-w-[480px] max-h-[90vh] overflow-y-auto scroll-modal rounded-t-2xl md:rounded-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 pt-6 pb-4">
            <h2 className="scroll-heading text-xl">Edit Profile</h2>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-(--scroll-bg-secondary) transition-colors"
            >
              <X size={18} className="text-(--scroll-text-secondary)" />
            </button>
          </div>

          {/* Profile Photo */}
          <div className="flex justify-center py-6">
            <button
              className="relative group"
              onClick={() => fileInputRef.current?.click()}
            >
              <img
                src={previewUrl || "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=160&h=160&fit=crop&crop=face"}
                alt="Profile"
                className="w-20 h-20 rounded-full object-cover border border-(--scroll-border)"
              />
              <div className="absolute inset-0 rounded-full bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <Camera size={20} className="text-white" />
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setAvatarFile(file);
                    setPreviewUrl(URL.createObjectURL(file));
                  }
                }}
              />
            </button>
          </div>

          {/* Fields */}
          <div className="px-6 pb-6">
            {/* Display Name */}
            <div className="edit-profile-field">
              <label className="edit-profile-label">Name</label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="scroll-input w-full"
                placeholder="Your display name"
                maxLength={50}
              />
            </div>

            {/* Username */}
            <div className="edit-profile-field">
              <label className="edit-profile-label">Username</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-(--scroll-text-tertiary) text-sm">@</span>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))}
                  className={`scroll-input w-full pl-8 ${isUsernameOnCooldown ? "opacity-50" : ""}`}
                  placeholder="username"
                  maxLength={30}
                  disabled={isUsernameOnCooldown}
                />
              </div>
              {isUsernameOnCooldown && (
                <p className="text-xs text-(--scroll-text-tertiary) mt-1">
                  Available to change {usernameCooldownEnd!.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                </p>
              )}
            </div>

            {/* Bio */}
            <div className="edit-profile-field">
              <label className="edit-profile-label">Bio</label>
              <textarea
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                className="scroll-input w-full resize-none"
                style={{ minHeight: "120px" }}
                placeholder="Tell your story..."
                maxLength={300}
              />
              <span className="text-xs text-(--scroll-text-tertiary) mt-1 block text-right">
                {bio.length}/300
              </span>
            </div>

            {/* Link */}
            <div className="edit-profile-field">
              <label className="edit-profile-label">Link</label>
              <div className="relative">
                <LinkIcon size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-(--scroll-text-tertiary)" />
                <input
                  type="url"
                  value={link}
                  onChange={(e) => setLink(e.target.value)}
                  className="scroll-input w-full pl-10"
                  placeholder="https://yourwebsite.com"
                />
              </div>
            </div>

            {/* Accent Color — free for all users */}
            <div className="edit-profile-field">
              <label className="edit-profile-label">Accent Color</label>
              <div className="flex gap-2.5 flex-wrap">
                {ACCENT_PRESETS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setAccentColor(color)}
                    className="w-8 h-8 rounded-full transition-shadow"
                    style={{
                      backgroundColor: color,
                      boxShadow: accentColor === color ? `0 0 0 2px var(--scroll-bg), 0 0 0 4px ${color}` : "none",
                    }}
                  />
                ))}
              </div>
            </div>

            {/* Profile theme — Tier 1 customization, free for all users */}
            <div className="edit-profile-field">
                <label className="edit-profile-label">Profile background</label>
                <div className="flex gap-2.5 flex-wrap">
                  <button
                    type="button"
                    onClick={() => setTheme((t) => { const { backgroundColor, backgroundGradient, ...rest } = t; return rest; })}
                    className="w-9 h-9 rounded-xl border border-(--scroll-border) bg-(--scroll-bg) text-(--scroll-text-tertiary) text-xs flex items-center justify-center transition-shadow"
                    style={{ boxShadow: !theme.backgroundColor && !theme.backgroundGradient ? `0 0 0 2px var(--scroll-bg), 0 0 0 4px var(--scroll-accent)` : "none" }}
                    title="None"
                  >
                    Off
                  </button>
                  {PROFILE_BACKGROUND_PRESETS.map((preset) => {
                    // Case-insensitive: stored hex is lowercased by sanitizeProfileTheme, presets are uppercase.
                    const selected = theme.backgroundGradient?.from?.toLowerCase() === preset.gradient.from.toLowerCase() && theme.backgroundGradient?.to?.toLowerCase() === preset.gradient.to.toLowerCase();
                    return (
                      <button
                        key={preset.id}
                        type="button"
                        onClick={() => setTheme((t) => ({ ...t, backgroundGradient: preset.gradient, backgroundColor: null }))}
                        className="w-9 h-9 rounded-xl transition-shadow"
                        style={{
                          background: profileThemeBackgroundCss({ backgroundGradient: preset.gradient }) ?? undefined,
                          boxShadow: selected ? `0 0 0 2px var(--scroll-bg), 0 0 0 4px ${preset.gradient.from}` : "none",
                        }}
                        title={preset.label}
                      />
                    );
                  })}
                </div>

                <label className="edit-profile-label mt-4">Theme style</label>
                <div className="flex gap-2">
                  {PROFILE_CARD_STYLES.map((style) => {
                    // normalizeCardStyle migrates a legacy stored "glass" to "yourspace" so the pill highlights.
                    const selected = (normalizeCardStyle(theme.cardStyle) ?? "default") === style;
                    return (
                      <button
                        key={style}
                        type="button"
                        onClick={() => setTheme((t) => ({ ...t, cardStyle: style }))}
                        className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${selected ? "border-(--scroll-accent) text-(--scroll-text) bg-(--scroll-bg-secondary)" : "border-(--scroll-border) text-(--scroll-text-secondary)"}`}
                      >
                        {CARD_STYLE_LABELS[style]}
                      </button>
                    );
                  })}
                </div>

                <label className="edit-profile-label mt-4">Display font</label>
                <div className="flex gap-2 flex-wrap">
                  {PROFILE_FONT_PRESETS.map((font) => {
                    const selected = (theme.font ?? "default") === font;
                    return (
                      <button
                        key={font}
                        type="button"
                        onClick={() => setTheme((t) => ({ ...t, font }))}
                        className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${selected ? "border-(--scroll-accent) text-(--scroll-text) bg-(--scroll-bg-secondary)" : "border-(--scroll-border) text-(--scroll-text-secondary)"}`}
                      >
                        {FONT_LABELS[font]}
                      </button>
                    );
                  })}
                </div>
              </div>

            {/* Error */}
            {(updateMutation.isError || uploadError) && (
              <p className="text-sm text-red-600 mb-4">
                {uploadError || parseApiError((updateMutation.error as Error)?.message)}
              </p>
            )}

            {/* Save Button */}
            <button
              onClick={handleSave}
              disabled={updateMutation.isPending || uploadingAvatar}
              className="scroll-btn-primary w-full py-3 text-sm mt-2 disabled:opacity-50"
            >
              {uploadingAvatar ? "Uploading..." : updateMutation.isPending ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
